import { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Phone, Calendar, Mail, FileText, Clock, Briefcase, Pencil, UserCircle2, Video, Users, Cloud, AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = { tarea: FileText, llamada: Phone, mensaje: Mail, email: Mail, reunion: Calendar };

const STATUS_LABELS = {
    pendiente: "Pendiente",
    en_progreso: "En progreso",
    completada: "Completada",
};

const STATUS_BADGE = {
    pendiente: "bg-slate-100 text-slate-600",
    en_progreso: "bg-blue-50 text-blue-700",
    completada: "bg-green-50 text-green-700",
};

const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Tasks() {
    const { user: me, hasPermission } = useAuth();
    const canManage = hasPermission("tasks_manage");
    const canViewDeals = hasPermission("deals_view");
    const [items, setItems] = useState([]);
    const [deals, setDeals] = useState([]);
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});

    const load = async () => {
        const [{data: tasks}, {data: dealList}, {data: userList}] = await Promise.all([
            // Tasks section shows ONLY CRM-created tasks. Google Calendar events
            // live exclusively in the Calendar section.
            api.get("/tasks", { params: { source: "crm" } }),
            canViewDeals ? api.get("/deals") : Promise.resolve({ data: [] }),
            api.get("/admin/users/lite").catch(()=>({ data: [] })),
        ]);
        setItems(tasks);
        setDeals(dealList);
        setUsers(userList || []);
    };
    useEffect(() => { load(); }, []);

    const dealTitle = (task) => deals.find(d => d.deal_id === task.deal_id)?.title || task.deal_title || null;
    const userName = (id) => users.find(u => u.user_id === id)?.name || (id === me?.user_id ? me?.name : null);

    const openNew = () => {
        if (!canManage) return;
        setEditing(null);
        setForm({ type: "tarea", priority: "media", status: "pendiente", assignee_id: me?.user_id });
        setOpen(true);
    };

    const openEdit = (t) => {
        if (!canManage) return;
        setEditing(t);
        setForm({
            ...t,
            due_date: toLocalInput(t.due_date),
            status: t.status || (t.completed ? "completada" : "pendiente"),
            assignee_id: t.assignee_id || t.owner_id || me?.user_id,
        });
        setOpen(true);
    };

    const save = async () => {
        if (!canManage) return;
        try {
            const payload = { ...form };
            if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
            // Deal is mandatory for CRM tasks — they must be tied to a deal.
            if (!payload.deal_id || payload.deal_id === "__none__") {
                toast.error("Debes vincular la tarea a un deal");
                return;
            }
            if (payload.assignee_id === "__none__") payload.assignee_id = null;
            if (editing) {
                await api.patch(`/tasks/${editing.task_id}`, payload);
                toast.success("Tarea actualizada");
            } else {
                await api.post("/tasks", payload);
                toast.success("Tarea creada");
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const toggle = async (t) => {
        if (!canManage) return;
        const nextStatus = t.completed ? "pendiente" : "completada";
        await api.patch(`/tasks/${t.task_id}`, { status: nextStatus });
        load();
    };

    const remove = async (id) => {
        if (!canManage) return;
        if (!window.confirm("¿Eliminar tarea?")) return;
        await api.delete(`/tasks/${id}`);
        toast.success("Eliminada"); load();
    };

    const pending = useMemo(() => items.filter(t => !t.completed), [items]);
    const done = useMemo(() => items.filter(t => t.completed), [items]);
    const now = Date.now();
    const overdue = useMemo(
        () => pending.filter(t => t.due_date && new Date(t.due_date).getTime() < now),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pending]
    );
    const upcoming = useMemo(
        () => pending.filter(t => !t.due_date || new Date(t.due_date).getTime() >= now),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pending]
    );

    // Multi-selection for the Overdue tab — independent from per-task delete.
    const [tab, setTab] = useState("upcoming");
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    // Drop selections that disappear (after deletion or sync) and when switching tab.
    useEffect(() => {
        if (tab !== "overdue") { if (selected.size) setSelected(new Set()); return; }
        const valid = new Set(overdue.map(t => t.task_id));
        let changed = false;
        const next = new Set();
        selected.forEach((id) => { if (valid.has(id)) next.add(id); else changed = true; });
        if (changed) setSelected(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, overdue]);

    const toggleSelect = (id) => {
        if (!canManage) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (!canManage) return;
        if (selected.size === overdue.length) setSelected(new Set());
        else setSelected(new Set(overdue.map(t => t.task_id)));
    };

    const deleteSelected = async () => {
        if (!canManage) return;
        if (selected.size === 0) return;
        if (!window.confirm(`¿Eliminar ${selected.size} tarea${selected.size > 1 ? "s" : ""} vencida${selected.size > 1 ? "s" : ""}? Esta acción no se puede deshacer.`)) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selected);
            const results = await Promise.allSettled(ids.map((id) => api.delete(`/tasks/${id}`)));
            const ok = results.filter(r => r.status === "fulfilled").length;
            const fail = results.length - ok;
            if (ok) toast.success(`${ok} tarea${ok > 1 ? "s eliminadas" : " eliminada"}`);
            if (fail) toast.error(`No se pudieron eliminar ${fail}`);
            setSelected(new Set());
            await load();
        } finally {
            setBulkBusy(false);
        }
    };

    const completeSelected = async () => {
        if (!canManage) return;
        if (selected.size === 0) return;
        if (!window.confirm(`¿Marcar ${selected.size} tarea${selected.size > 1 ? "s" : ""} como completada${selected.size > 1 ? "s" : ""}?`)) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selected);
            const results = await Promise.allSettled(
                ids.map((id) => api.patch(`/tasks/${id}`, { status: "completada" }))
            );
            const ok = results.filter(r => r.status === "fulfilled").length;
            const fail = results.length - ok;
            if (ok) toast.success(`${ok} tarea${ok > 1 ? "s completadas" : " completada"} · ¡buen trabajo!`);
            if (fail) toast.error(`No se pudieron actualizar ${fail}`);
            setSelected(new Set());
            await load();
        } finally {
            setBulkBusy(false);
        }
    };

    // Renders a single pending task row. ``selectable`` adds a left-side
    // selection checkbox alongside the existing "complete" checkbox.
    const renderTaskRow = (t, { selectable = false } = {}) => {
        const Icon = TYPES[t.type] || FileText;
        const isOverdue = t.due_date && new Date(t.due_date).getTime() < Date.now();
        const linkedDeal = dealTitle(t);
        const assignee = userName(t.assignee_id || t.owner_id);
        const status = t.status || "pendiente";
        const isSelected = selected.has(t.task_id);
        return (
            <div
                key={t.task_id}
                className={`bg-white border rounded-lg p-3 sm:p-4 flex items-start sm:items-center gap-2 sm:gap-3 transition-colors ${isSelected ? "border-orange-400 bg-orange-50/30" : "border-slate-200"}`}
                data-testid={`task-${t.task_id}`}
            >
                {selectable && (
                    <span
                        className="mt-1 sm:mt-0 shrink-0 inline-flex items-center justify-center"
                        title="Seleccionar para acciones masivas (eliminar / completar)"
                    >
                        <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(t.task_id)}
                            aria-label="Seleccionar tarea vencida"
                            data-testid={`task-select-${t.task_id}`}
                            className="h-5 w-5 rounded border-slate-400 data-[state=checked]:bg-slate-700 data-[state=checked]:border-slate-700"
                        />
                    </span>
                )}
                <span
                    className="mt-1 sm:mt-0 shrink-0 inline-flex items-center justify-center"
                    title={t.completed ? "Marcar como pendiente" : "Marcar como completada"}
                >
                    <Checkbox
                        checked={t.completed}
                        onCheckedChange={() => toggle(t)} disabled={!canManage}
                        data-testid={`task-toggle-${t.task_id}`}
                        aria-label={t.completed ? "Marcar como pendiente" : "Marcar como completada"}
                        className="h-5 w-5 rounded-full border-green-500 hover:border-green-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                    />
                </span>
                <div className="w-9 h-9 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><Icon className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900 break-words">{t.title}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        {t.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${isOverdue ? "text-red-600" : "text-slate-500"}`}>
                                <Clock className="w-3 h-3 shrink-0"/>{new Date(t.due_date).toLocaleString()}
                            </span>
                        )}
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_BADGE[status]}`} data-testid={`task-status-${t.task_id}`}>
                            {STATUS_LABELS[status]}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{t.priority}</span>
                        {t.source === "gcal" ? (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700 inline-flex items-center gap-1" title="Importada desde Google Calendar" data-testid={`task-source-${t.task_id}`}>
                                <Cloud className="w-3 h-3"/>Google
                            </span>
                        ) : (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-orange-50 text-orange-700" title="Creada en el CRM" data-testid={`task-source-${t.task_id}`}>
                                CRM
                            </span>
                        )}
                        {linkedDeal && (
                            <span className="text-xs flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-0.5 rounded max-w-full truncate" data-testid={`task-deal-${t.task_id}`}>
                                <Briefcase className="w-3 h-3 shrink-0"/><span className="truncate">{linkedDeal}</span>
                            </span>
                        )}
                        {assignee && (
                            <span className="text-xs flex items-center gap-1 text-slate-600 max-w-full truncate" data-testid={`task-assignee-${t.task_id}`}>
                                <UserCircle2 className="w-3 h-3 shrink-0"/><span className="truncate">{assignee}</span>
                            </span>
                        )}
                        {t.meet_link && (
                            <a href={t.meet_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                className="text-xs flex items-center gap-1 text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded transition-colors"
                                data-testid={`task-meet-${t.task_id}`}>
                                <Video className="w-3 h-3 shrink-0"/>Unirse
                            </a>
                        )}
                        {Array.isArray(t.attendees) && t.attendees.length > 0 && (
                            <span className="text-xs flex items-center gap-1 text-slate-500" data-testid={`task-attendees-${t.task_id}`}
                                title={t.attendees.map(a => a.email || a).join(", ")}>
                                <Users className="w-3 h-3 shrink-0"/>{t.attendees.length}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center shrink-0">
                    {canManage && <><Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`task-edit-${t.task_id}`} className="h-8 w-8 sm:h-9 sm:w-9"><Pencil className="w-4 h-4 text-slate-600"/></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(t.task_id)} data-testid={`task-delete-${t.task_id}`} className="h-8 w-8 sm:h-9 sm:w-9"><Trash2 className="w-4 h-4 text-red-500"/></Button></>}
                </div>
            </div>
        );
    };

    return (
        <Layout
            title="Tareas y seguimientos"
            subtitle="Tu plan comercial diario"
            actions={canManage ? <Button onClick={openNew} data-testid="task-new-button"><Plus className="w-4 h-4 mr-2"/>Nueva Tarea</Button> : null}
        >
            <div className="space-y-6">
                {/* Tabs: Próximas / Vencidas */}
                <div className="flex items-center justify-between flex-wrap gap-2" data-testid="tasks-tabs">
                    <div className="inline-flex bg-slate-100 rounded-md p-1">
                        <button
                            type="button"
                            onClick={() => setTab("upcoming")}
                            data-testid="tasks-tab-upcoming"
                            className={`px-3 py-1.5 text-sm rounded inline-flex items-center gap-2 transition-colors ${tab === "upcoming" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                        >
                            <CalendarClock className="w-4 h-4" />Próximas
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === "upcoming" ? "bg-orange-100 text-orange-700" : "bg-slate-200 text-slate-700"}`}>{upcoming.length}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setTab("overdue")}
                            data-testid="tasks-tab-overdue"
                            className={`px-3 py-1.5 text-sm rounded inline-flex items-center gap-2 transition-colors ${tab === "overdue" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                        >
                            <AlertTriangle className="w-4 h-4 text-red-600" />Vencidas
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === "overdue" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-700"}`}>{overdue.length}</span>
                        </button>
                    </div>
                    {tab === "overdue" && overdue.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap" data-testid="tasks-bulk-bar">
                            <label
                                className="text-xs text-slate-600 flex items-center gap-1.5 select-none cursor-pointer"
                                title="Marca todas las tareas vencidas para acciones masivas"
                            >
                                <Checkbox
                                    checked={selected.size > 0 && selected.size === overdue.length}
                                    onCheckedChange={toggleSelectAll}
                                    aria-label="Seleccionar todas las vencidas"
                                    data-testid="tasks-select-all-overdue"
                                    className="border-slate-400 data-[state=checked]:bg-slate-700 data-[state=checked]:border-slate-700"
                                />
                                Seleccionar todas
                            </label>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={completeSelected}
                                disabled={selected.size === 0 || bulkBusy}
                                title="Marcar las tareas seleccionadas como completadas"
                                data-testid="tasks-bulk-complete"
                                className="border-green-500 text-green-700 hover:bg-green-50 hover:text-green-700 disabled:border-slate-200 disabled:text-slate-400"
                            >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Marcar como completadas{selected.size ? ` (${selected.size})` : ""}
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={deleteSelected}
                                disabled={selected.size === 0 || bulkBusy}
                                title="Eliminar permanentemente las tareas seleccionadas"
                                data-testid="tasks-bulk-delete"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar seleccionadas{selected.size ? ` (${selected.size})` : ""}
                            </Button>
                        </div>
                    )}
                </div>

                {tab === "upcoming" ? (
                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3" data-testid="tasks-upcoming-heading">
                            Próximas ({upcoming.length})
                        </h3>
                        <div className="space-y-2">
                            {upcoming.map((t) => renderTaskRow(t))}
                            {upcoming.length === 0 && (
                                <div className="text-slate-400 text-sm py-6 text-center bg-white border border-slate-200 rounded-lg" data-testid="tasks-upcoming-empty">
                                    Sin tareas próximas
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-3 inline-flex items-center gap-1.5" data-testid="tasks-overdue-heading">
                            <AlertTriangle className="w-3.5 h-3.5" />Vencidas ({overdue.length})
                        </h3>
                        <div className="space-y-2">
                            {overdue.map((t) => renderTaskRow(t, { selectable: canManage }))}
                            {overdue.length === 0 && (
                                <div className="text-slate-400 text-sm py-6 text-center bg-white border border-slate-200 rounded-lg" data-testid="tasks-overdue-empty">
                                    No hay tareas vencidas. ¡Buen trabajo!
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {done.length > 0 && (
                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Completadas ({done.length})</h3>
                        <div className="space-y-2">
                            {done.map((t) => (
                                <div key={t.task_id} className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4 flex items-start sm:items-center gap-2 sm:gap-3 opacity-70">
                                    <Checkbox checked onCheckedChange={()=>toggle(t)} className="mt-1 sm:mt-0 shrink-0"/>
                                    <div className="flex-1 line-through text-slate-500 text-sm min-w-0 break-words">{t.title}</div>
                                    <div className="flex items-center shrink-0">
                                        {canManage && <><Button variant="ghost" size="icon" onClick={()=>openEdit(t)} data-testid={`task-edit-${t.task_id}`} className="h-8 w-8 sm:h-9 sm:w-9"><Pencil className="w-4 h-4 text-slate-600"/></Button>
                                        <Button variant="ghost" size="icon" onClick={()=>remove(t.task_id)} className="h-8 w-8 sm:h-9 sm:w-9"><Trash2 className="w-4 h-4 text-red-500"/></Button></>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Editar tarea" : "Nueva tarea"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Título *</Label><Input value={form.title||""} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="task-form-title"/></div>
                        <div><Label>Descripción</Label><Textarea value={form.description||""} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="task-form-description"/></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Tipo</Label>
                                <Select value={form.type||"tarea"} onValueChange={(v)=>setForm({...form, type: v})}>
                                    <SelectTrigger data-testid="task-form-type"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="tarea">Tarea</SelectItem>
                                        <SelectItem value="llamada">Llamada</SelectItem>
                                        <SelectItem value="email">Email</SelectItem>
                                        <SelectItem value="mensaje">Mensaje</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>Prioridad</Label>
                                <Select value={form.priority||"media"} onValueChange={(v)=>setForm({...form, priority: v})}>
                                    <SelectTrigger data-testid="task-form-priority"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="alta">Alta</SelectItem>
                                        <SelectItem value="media">Media</SelectItem>
                                        <SelectItem value="baja">Baja</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2"><Label>Fecha y hora</Label><Input type="datetime-local" value={form.due_date||""} onChange={(e)=>setForm({...form, due_date: e.target.value})} data-testid="task-form-due"/></div>
                            <div><Label>Estado</Label>
                                <Select value={form.status||"pendiente"} onValueChange={(v)=>setForm({...form, status: v})}>
                                    <SelectTrigger data-testid="task-form-status"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pendiente">Pendiente</SelectItem>
                                        <SelectItem value="en_progreso">En progreso</SelectItem>
                                        <SelectItem value="completada">Completada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>Asignado a</Label>
                                <Select value={form.assignee_id || "__none__"} onValueChange={(v)=>setForm({...form, assignee_id: v})}>
                                    <SelectTrigger data-testid="task-form-assignee"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— Sin asignar —</SelectItem>
                                        {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2"><Label>Deal vinculado *</Label>
                                <Select value={form.deal_id || ""} onValueChange={(v)=>setForm({...form, deal_id: v})}>
                                    <SelectTrigger data-testid="task-form-deal"><SelectValue placeholder="Selecciona un deal"/></SelectTrigger>
                                    <SelectContent>
                                        {deals.map(d => <SelectItem key={d.deal_id} value={d.deal_id}>{d.title}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">Toda tarea debe estar vinculada a un deal.</p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={()=>setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="task-form-save">{editing ? "Guardar cambios" : "Crear tarea"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
